import { AllowableCardTypes } from '../session/payment-rule-response-v1.model';
import { ContactInfoSettings } from './contact-info.model';
import { CustomTemplatesSettings } from '../payment-settings/custom-templates-settings.model';

export interface PaymentAcceptanceRules {
  domain: Domain;
  enhancedAutopaySettings: EnhancedAutopaySettings;
  cardSettings: CardSettings;
  /** Deprecated. Remove once all the bank changes in PARS have been deployed to PROD */
  achSettings: AchSettings;
  bankSettings: BankSettings;
  adjustmentsSettings: AdjustmentsSettings;
  surchargingSettings: SurchargingSettings;
  generalPaymentSettings: GeneralPaymentSettings;
  paymentLinksSettings: PaymentLinksSettings;
  gracePeriodSettings: GracePeriodSettings;
  flexibleTemplateSettings: FlexibleTemplateSettings;
  hostedPaymentsValidatingSettings: HostedPaymentsPageSettings;
  hostedPaymentsNonValidatingSettings: HostedPaymentsPageSettings;
  cvvCardOnFileSettings: CvvCardOnFileSettings;
  statementPaymentsSettings: StatementPaymentsSettings;
  contactInfoSettings: ContactInfoSettings;
  timedCardAcceptanceSettings: TimedCardAcceptanceSettings;
  stripeSettings: StripeSettings;
  customTemplatesSettings?: CustomTemplatesSettings[];
}

export interface Domain {
  tenantId?: string;
  mid?: string;
  gatewayName?: string;
  bankMerchantAccountId?: string;
  originApplication?: string;
}

export interface CardSettings {
  maxPaymentAmount?: number;
  acceptedCardBrands?: AllowableCardTypes[];
  applications?: CardApplicationSettings[];
}

export interface CardApplicationSettings {
  originApplication?: string;
  originApplicationDisplayName?: string;
  requireCVV: boolean;
  allowCardPayments?: boolean;
}

/** Deprecated. Remove once all the bank changes in PARS have been deployed to PROD */
export interface AchSettings {
  applications?: AchApplicationSettings[];
}

/** Deprecated. Remove once all the bank changes in PARS have been deployed to PROD */
export interface AchApplicationSettings {
  originApplication?: string;
  allowACHPayments?: boolean;
}

export interface BankSettings {
  applications?: BankApplicationSettings[];
}

export interface BankApplicationSettings {
  originApplication?: string;
  allowBankPayments?: boolean;
}

export interface AdjustmentsSettings {
  shortAndOverPay: ShortAndOverPaySettings;
}

export interface ShortAndOverPaySettings {
  emails?: string[];
  allowDisputeReason?: boolean;
  allowNonDisputeReason?: boolean;
  allowFreeFormReason?: boolean;
  applications?: ShortAndOverPayApplicationSettings[];
}

export interface ShortAndOverPayApplicationSettings {
  name?: string;
  shortPay?: ShortPayApplicationSettings;
  overPay?: OverPayApplicationSettings;
}

export interface ShortPayApplicationSettings {
  enabled?: boolean;
}

export interface OverPayApplicationSettings {
  enabled?: boolean;
}

export interface SurchargingSettings {
  allowSurcharging?: boolean;
  restrictByPostalCode?: boolean;
  restrictByCardType?: boolean;
}

export interface GeneralPaymentSettings {
  useSavedPaymentMethod?: boolean;
  createSavedPaymentMethod?: boolean;
  zeroDollarPaymentsEnabled?: boolean;
  blockDuplicatePayments?: boolean;
}

export interface PaymentLinksSettings {
  daysUntilLinkExpiration?: number;
  includeContactInfo?: boolean;
  contactInfoMessage?: string;
  allowBuyerLogin?: boolean;
}

export interface PaymentInclusionExclusionSetting {
  allowCreditCard: boolean;
  allowACH: boolean;
}

export interface PaymentExclusionOverrideSetting {
  isBuyerAccountInRestrictionList: boolean;
  allowRestrictionOverride: boolean;
}

export interface GracePeriodSettings {
  applications?: GracePeriodApplicationSettings[];
  gracePeriodType: string;
  gracePeriodNumberOfDays: number;
}

export interface GracePeriodApplicationSettings {
  originApplication?: string;
  enabled: boolean;
  surchargingEnabled: boolean;
}

export interface FlexibleTemplateSettings {
  restrictionType?: FlexibleTemplateRestrictionType;
  applications?: FlexibleTemplateApplicationSettings[];
  templateRules?: FlexibleTemplateRules[];
  deactivatedTemplateIds?: number[];
}

export type FlexibleTemplateRestrictionType =
  | 'NoRestriction'
  | 'FlexibleTemplateRestriction';

export interface FlexibleTemplateApplicationSettings {
  originApplication?: string;
  originApplicationDisplayName?: string;
  allowFlexibleTemplate?: boolean;
}

export interface FlexibleTemplateRules {
  templateId?: number;
  templateName?: string;
  templateType?: string;
  allowCardPayments?: boolean;
  allowBankPayments?: boolean;
  allowSurcharging?: boolean;
}

export interface HostedPaymentsPageSettings {
  buyerCustomIdentifierFieldLabel?: string;
  captchaEnabled?: boolean;
  hostedPageUrl?: string;
  hostedPaymentNotes?: HostedPaymentNotes[];
}

export interface HostedPaymentNotes {
  enabled?: boolean;
  label?: string;
  required?: boolean;
  type?: HostedPaymentNotesType;
  values?: string[];
}

export enum HostedPaymentNotesType {
  FreeTextNote = 'FreeTextNote',
  FixedNote = 'FixedNote',
}

export interface CvvCardOnFileSettings {
  applications: CvvCardApplicationSettings[];
}

export interface CvvCardApplicationSettings {
  originApplication: string;
  originApplicationDisplayName: string;
  requireCvvCardOnFile: boolean;
}

export interface StatementPaymentsSettings {
  applications: StatementPaymentsApplicationSettings[];
}

export interface StatementPaymentsApplicationSettings {
  originApplication: string;
  originApplicationDisplayName: string;
  allowStatementPayments: boolean;
}

export interface EnhancedAutopaySettings {
  autoPayVersion: AutoPayVersionType;
}

export enum AutoPayVersionType {
  None = 'None',
  Single = 'Single',
  Batched = 'Batched',
}

export interface TimedCardAcceptanceSettings {
  timedCardAcceptanceType?: 'NoGracePeriod' | 'DocumentDate' | 'DueDate';
  timedCardAcceptanceNumberOfDays?: number;
  applications: TimedCardAcceptanceApplicationSettings[];
}

export interface TimedCardAcceptanceApplicationSettings {
  originApplication: string;
  enabled?: boolean;
  cardEnabled?: boolean;
}

export interface StripeSettings {
  applications: StripeApplicationSettings[];
}

export interface StripeApplicationSettings {
  originApplication: string;
  allowStripePayments: boolean;
}
